import {Dimensions, Platform, StyleSheet} from 'react-native';
import React, {useCallback, useState} from 'react';

import {Analytics} from '../../../../../src/config/analytics/analytics';
import {Box} from '../../../../../src/components/core/view';
import {DeliveryLocation} from '../../../../../src/driver/types/orderDetails';
import {DriverScreens} from '../../../../../src/driver/navigation/type';
import {EventType} from '@buncha/config/analytics/type';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '../../../../../src/components/conditional/If';
import {MainScreens} from '@buncha/navigation/types';
import {Modules} from '@buncha/utils/types';
import {PButton} from '../../../../../src/components/core/Button';
import {SpinnerComponent} from '../../../../components/core/loader';
import {Task} from '@buncha/config/onfleet/types';
import {getWidth} from '../../../../../src/utils/common';
import listener from '@buncha/config/listener';
import {navigation} from '../../../../../src/config/navigation';
import onfleetService from '@buncha/config/onfleet';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useColorThemeType} from '../../../../../src/appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

interface DeliveryButtonComponentProps {
  deliveryLocation: DeliveryLocation | undefined;
  orderId: string;
  storeId: number;
  isActive: boolean;
  task: Task | undefined;
}

const DeliveryButtonComponent: React.FC<
  DeliveryButtonComponentProps
> = props => {
  const theme = useColorThemeType('main');
  const routeParams: any = useRoute().params;
  const deliveryLocation = props?.deliveryLocation;
  const runId = deliveryLocation?.runId || 0;
  const orderId = Number(props?.orderId) || 0;
  const task = props?.task;
  const {button} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const [loading, setLoading] = useState(false);
  const activeTaskParams = useAppSelector(
    gState => gState.home.activeTaskParams,
  );
  const isActiveTask = props?.isActive;

  const startActiveTask = useCallback(() => {
    if (activeTaskParams)
      return navigation.replace(DriverScreens.OrderDetails, activeTaskParams);
  }, [activeTaskParams]);

  const changeStatusToOnTheWay = useCallback(async () => {
    if (!task?.id) return;
    setLoading(true);
    const res = await onfleetService.sendOnTheWay(task.id);
    setLoading(false);
    if (res) listener.emit('onTheWay');
  }, [task?.id]);

  const navigateToDeliveryDetailsPage = useCallback(() => {
    Analytics.eventWithProps('Navigate To Delivery Details Page', {
      type: EventType.cta,
      orderId: orderId,
      userType: 'driver',
    });
    navigation.navigate(MainScreens.Driver, {
      screen: DriverScreens.DeliveryDetails,
      params: {
        orderId: orderId,
        tutorial: routeParams?.tutorial,
        runId: runId,
        taskId: routeParams?.taskId,
      },
    });
  }, [orderId, routeParams?.taskId, routeParams?.tutorial, runId]);

  return (
    <Box
      style={[
        styles.parentBox,
        {
          borderColor: theme?.text?.gray?.[200],
          backgroundColor: theme?.background?.white?.[100],
        },
      ]}>
      <If condition={task?.state}>
        <PButton
          style={styles.bottomCTA}
          title={button?.tapComplete}
          onPress={navigateToDeliveryDetailsPage}
        />
      </If>
      <If condition={!task?.state}>
        <If condition={isActiveTask}>
          <PButton
            style={styles.bottomCTA}
            title={button?.startTask}
            onPress={changeStatusToOnTheWay}
            disabled={loading}
            isLoading={loading}
            bgColor={theme?.background?.blue?.[100]}
            disabledBgColor={theme?.background?.blue?.[200]}
            loadingIndicator={<SpinnerComponent />}
          />
        </If>
        <If condition={!isActiveTask}>
          <PButton
            style={styles.bottomCTA}
            title={button?.openFirstTask}
            onPress={startActiveTask}
            bgColor={theme?.background?.blue?.[100]}
          />
        </If>
      </If>
    </Box>
  );
};

export default React.memo(DeliveryButtonComponent);

const styles = StyleSheet.create({
  parentBox: {
    paddingTop: 15,
    borderTopWidth: 1,
    width: getWidth(Dimensions?.get('window')?.width),
  },
  bottomCTA: {
    width: '90%',
    alignSelf: 'center',
    borderRadius: 50,
    marginBottom: Platform.select({
      android: 15,
      ios: 0,
    }),
  },
});
